
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, User, AlertCircle, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@shared/store/authStore';
import { useUIStore } from '@shared/store/uiStore';
import { ForgotPasswordModal } from '@shared/components/modals/ForgotPasswordModal';
import { ROUTES } from '@app/routes';

interface LoginPageProps {
  onLogin?: () => void;
  onBack?: () => void;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const { login, isLoading, error, clearError } = useAuthStore();
  const themeClass = useUIStore((state) => state.themeClass);

  // Check if we're in a light theme (dynamics or light) - hide decorative elements
  const isLightTheme = themeClass === 'theme-dynamics' || themeClass === 'theme-light';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    // Validation
    if (!username || !password) {
      return;
    }

    if (password.length < 8) {
      return;
    }

    try {
      await login(username, password, rememberMe);
      if (onLogin) {
        onLogin();
      } else {
        // Check for returnUrl query param first (from RequireAuth redirects)
        const searchParams = new URLSearchParams(location.search);
        const returnUrl = searchParams.get('returnUrl');

        // Then check location state (from AppShell redirects)
        const stateFrom = (location.state as any)?.from;

        // Use returnUrl first, then state.from, then default to /app
        const from = returnUrl || stateFrom || ROUTES.APP;

        // Validate it's a safe internal route (security check)
        // Allow /app/* routes and specific standalone protected routes
        const allowedPatterns = ['/app/', '/vibe-remote', '/cli-dashboard', '/terminal/connect'];
        const isValidRoute = allowedPatterns.some(pattern => from.startsWith(pattern));
        const redirectTo = isValidRoute ? from : ROUTES.APP;

        navigate(redirectTo, { replace: true });
      }
    } catch (err) {
      // Error is already handled by authStore
      console.error('Login failed:', err);
    }
  };

  return (
    <>
      <ForgotPasswordModal visible={showForgotPassword} onClose={() => setShowForgotPassword(false)} />
      <div className="min-h-screen bg-dark-bg flex items-center justify-center relative overflow-hidden px-4 py-8">

        {/* Background Elements - subtle gradient glows (only shown on dark themes) */}
        {!isLightTheme && (
          <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-brand-600/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-brand-600/5 rounded-full blur-3xl" />
          </div>
        )}

      <div className="w-full max-w-md bg-gradient-to-br from-dark-surface to-dark-bg/80 border border-dark-border/60 rounded-xl shadow-2xl shadow-black/20 relative z-10 flex flex-col mx-auto">

        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-dark-border/60 text-center">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-brand-500/20 to-brand-600/10 border border-brand-500/30 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-brand-900/20">
            <Lock size={28} className="sm:w-8 sm:h-8 text-brand-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">Welcome Back</h2>
          <p className="text-sm text-dark-muted mt-2">Sign in to access your dashboard</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 sm:p-8 space-y-6">
          {error && (
            <div data-testid="login-error" className={`px-4 py-3 rounded text-xs flex items-center gap-2 ${
              isLightTheme
                ? 'bg-red-50 border border-red-200 text-red-700'
                : 'bg-red-900/20 border border-red-900/50 text-red-300'
            }`}>
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div className="space-y-2">
            <label className={`text-xs font-bold uppercase tracking-wider ml-1 ${
              isLightTheme ? 'text-gray-600' : 'text-gray-400'
            }`}>Username</label>
            <div className="relative group">
              <User size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-brand-400 transition-colors z-10 ${
                isLightTheme ? 'text-gray-500' : 'text-gray-500'
              }`} />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                data-testid="username-input"
                className={`w-full border text-sm sm:text-base rounded pl-10 pr-4 py-3 min-h-[44px] focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all ${
                  isLightTheme
                    ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                    : 'bg-dark-input border-dark-border text-white placeholder:text-gray-500'
                }`}
                placeholder="Enter your username"
                data-ux='enter-your-username-edit' />
            </div>
          </div>

          <div className="space-y-2">
            <label className={`text-xs font-bold uppercase tracking-wider ml-1 ${
              isLightTheme ? 'text-gray-600' : 'text-gray-400'
            }`}>Password</label>
            <div className="relative group">
              <Lock size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 group-focus-within:text-brand-400 transition-colors z-10 ${
                isLightTheme ? 'text-gray-500' : 'text-gray-500'
              }`} />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="password-input"
                className={`w-full border text-sm sm:text-base rounded pl-10 pr-4 py-3 min-h-[44px] focus:ring-2 focus:ring-brand-500/50 focus:border-brand-500 outline-none transition-all ${
                  isLightTheme
                    ? 'bg-white border-gray-300 text-gray-900 placeholder:text-gray-400'
                    : 'bg-dark-input border-dark-border text-white placeholder:text-gray-500'
                }`}
                placeholder="Enter your password"
                data-ux='password-edit' />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 text-xs sm:text-sm">
            <label className={`flex items-center gap-2 cursor-pointer min-h-[44px] sm:min-h-0 ${
              isLightTheme ? 'text-gray-600 hover:text-gray-800' : 'text-gray-400 hover:text-gray-300'
            }`}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className={`w-4 h-4 rounded accent-brand-500 ${
                  isLightTheme ? 'bg-white border-gray-300' : 'bg-dark-input border-dark-border'
                }`}
                data-testid='login-field-checkbox'
                data-ux='toggle' />
              Remember me
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-brand-600 hover:text-brand-500 hover:underline min-h-[44px] sm:min-h-0 flex items-center"
              data-testid='login-forgot-password-btn'
              data-ux='forgot-password'>
              Forgot Password?
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            data-testid="login-submit"
            className={`
              w-full bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400
              text-white font-bold py-3 sm:py-3.5 rounded-lg shadow-md shadow-brand-900/20
              flex items-center justify-center gap-2 transition-all duration-200 min-h-[44px]
              ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'}
            `}
            data-ux='action-submit'>
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Authenticating...
              </>
            ) : (
              <>
                Sign In <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        {/* Footer - Only show if onBack is provided (landing page enabled) */}
        {onBack && (
          <div className={`p-4 sm:p-5 border-t text-center rounded-b-xl ${
            isLightTheme
              ? 'bg-gray-50 border-gray-200'
              : 'bg-dark-surface/50 border-dark-border/60'
          }`}>
            <button
              onClick={onBack}
              className={`text-xs sm:text-sm transition-all duration-200 min-h-[44px] flex items-center justify-center mx-auto px-4 py-2 rounded-lg hover:bg-dark-hover ${
                isLightTheme ? 'text-gray-500 hover:text-gray-700' : 'text-dark-muted hover:text-white'
              }`}
              data-testid='login-back-to-home-btn'
              data-ux='back-to-home'>
              &larr; Back to Home
            </button>
          </div>
        )}

      </div>
      </div>
    </>
  );
};
